/**
 * avatars/items/hair.js
 * Xây dựng hình học 3D cho createHair
 */

import * as THREE from 'three';
import { HAIR_STYLES } from './item-types.js';

export { HAIR_STYLES };

export function createHair(lib, style, headSize, hairColor = '#1e293b') {
  const hairGroup = new THREE.Group();
  hairGroup.name = `Hair_${style}`;
  const topHairMeshes = [];
  const sideHairMeshes = [];
  const backHairMeshes = [];
  const lowerStrandMeshes = [];
  hairGroup.userData = { style, topHairMeshes, sideHairMeshes, backHairMeshes, lowerStrandMeshes };

  const hairMat = new THREE.MeshStandardMaterial({
    color: hairColor,
    roughness: 0.85
  });
  lib.disposables.push(hairMat);

  if (style === 'bald') {
    return hairGroup;
  }

  if (style === 'bald' || style === 'neat_parted') {
    // Kiểu trọc hoặc tóc ngắn rẽ ngôi chuẩn: 100% texture phẳng trên khối đầu, 0 mesh 3D thừa
    return hairGroup;
  }

  if (style === 'elvis') {
    // Kiểu tóc Elvis Presley: Pompadour phồng cao phía trước trán bằng các khối voxel bậc thang
    const pompBaseGeo = new THREE.BoxGeometry(headSize * 0.86, headSize * 0.28, headSize * 0.6);
    const pompBase = new THREE.Mesh(pompBaseGeo, hairMat);
    pompBase.position.set(0, headSize * 1.06, headSize * 0.12);
    pompBase.castShadow = true;
    hairGroup.add(pompBase);
    topHairMeshes.push(pompBase);

    const pompTopGeo = new THREE.BoxGeometry(headSize * 0.72, headSize * 0.2, headSize * 0.42);
    const pompTop = new THREE.Mesh(pompTopGeo, hairMat);
    pompTop.position.set(0, headSize * 1.22, headSize * 0.18);
    pompTop.castShadow = true;
    hairGroup.add(pompTop);
    topHairMeshes.push(pompTop);

    const curlGeo = new THREE.BoxGeometry(headSize * 0.36, headSize * 0.14, headSize * 0.25);
    const curl = new THREE.Mesh(curlGeo, hairMat);
    curl.position.set(0, headSize * 1.04, headSize * 0.48);
    hairGroup.add(curl);
    topHairMeshes.push(curl);

    lib.disposables.push(pompBaseGeo, pompTopGeo, curlGeo);
  } else if (style === 'dan_truong') {
    // Kiểu tóc Đan Trường: Bổ luống 5:5 hai mái bồng bềnh rẽ hai bên trán
    [-1, 1].forEach((side) => {
      const bangsGeo = new THREE.BoxGeometry(headSize * 0.46, headSize * 0.28, headSize * 0.3);
      const bangs = new THREE.Mesh(bangsGeo, hairMat);
      bangs.position.set(side * headSize * 0.28, headSize * 0.86, headSize * 0.42);
      bangs.rotation.set(0.08, side * 0.15, side * 0.2);
      bangs.castShadow = true;
      hairGroup.add(bangs);
      topHairMeshes.push(bangs);
      lib.disposables.push(bangsGeo);
    });
  } else if (style === 'luu_duc_hoa') {
    // Kiểu tóc Lưu Đức Hoa: Rẽ ngôi 7:3 lịch lãm vuốt bồng một bên
    const mainPartGeo = new THREE.BoxGeometry(headSize * 0.68, headSize * 0.26, headSize * 0.5);
    const mainPart = new THREE.Mesh(mainPartGeo, hairMat);
    mainPart.position.set(headSize * 0.14, headSize * 1.0, headSize * 0.18);
    mainPart.rotation.set(-0.06, -0.12, -0.15);
    mainPart.castShadow = true;
    hairGroup.add(mainPart);
    topHairMeshes.push(mainPart);

    const smallPartGeo = new THREE.BoxGeometry(headSize * 0.32, headSize * 0.2, headSize * 0.45);
    const smallPart = new THREE.Mesh(smallPartGeo, hairMat);
    smallPart.position.set(-headSize * 0.34, headSize * 0.94, headSize * 0.15);
    hairGroup.add(smallPart);
    topHairMeshes.push(smallPart);

    lib.disposables.push(mainPartGeo, smallPartGeo);
  } else if (style === 'long_hair') {
    // Tóc dài suôn mượt buông xõa hai bên vai và sau lưng (giữ lại khi đội nón lá!)
    [-1, 1].forEach((side) => {
      const strandGeo = new THREE.BoxGeometry(headSize * 0.18, headSize * 0.95, headSize * 0.26);
      const strand = new THREE.Mesh(strandGeo, hairMat);
      strand.position.set(side * headSize * 0.46, headSize * 0.25, headSize * 0.16);
      hairGroup.add(strand);
      lowerStrandMeshes.push(strand);
      lib.disposables.push(strandGeo);
    });

    const longBackGeo = new THREE.BoxGeometry(headSize * 1.04, headSize * 1.15, headSize * 0.2);
    const longBack = new THREE.Mesh(longBackGeo, hairMat);
    longBack.position.set(0, headSize * 0.18, -headSize * 0.46);
    hairGroup.add(longBack);
    lowerStrandMeshes.push(longBack);
    lib.disposables.push(longBackGeo);
  } else if (style === 'spiky') {
    // Tóc vuốt dựng gai nhọn trẻ trung
    for (let i = -2; i <= 2; i++) {
      const spikeGeo = new THREE.BoxGeometry(headSize * 0.16, headSize * 0.24, headSize * 0.2);
      const spike = new THREE.Mesh(spikeGeo, hairMat);
      spike.position.set(i * headSize * 0.18, headSize * 1.04, (2 - Math.abs(i)) * 0.05);
      spike.rotation.set(-0.12, 0, -i * 0.16);
      hairGroup.add(spike);
      topHairMeshes.push(spike);
      lib.disposables.push(spikeGeo);
    }
  } else if (style === 'short_neat') {
    // Kiểu tóc ngắn gọn cho trẻ em (Short Neat Child Hair)
    const capGeo = new THREE.BoxGeometry(headSize * 1.02, headSize * 0.1, headSize * 1.02);
    const cap = new THREE.Mesh(capGeo, hairMat);
    cap.position.set(0, headSize * 1.0, 0);
    cap.castShadow = true;
    hairGroup.add(cap);
    topHairMeshes.push(cap);

    [-1, 0, 1].forEach((pos) => {
      const bangGeo = new THREE.BoxGeometry(headSize * 0.3, headSize * 0.12, headSize * 0.1);
      const bang = new THREE.Mesh(bangGeo, hairMat);
      bang.position.set(pos * headSize * 0.28, headSize * 0.94, headSize * 0.46);
      bang.castShadow = true;
      hairGroup.add(bang);
      topHairMeshes.push(bang);
      lib.disposables.push(bangGeo);
    });

    lib.disposables.push(capGeo);
  }

  return hairGroup;
}
